import config from './config'
import { callAuthenticatedApi } from './authenticated-api'

const apiUrls = {
  remote: config.passesRemoteApiUrl,
  local: config.passesLocalApiUrl
}

const apiError = (err, fallbackMessage) => err.response?.data ?? { ok: false, error: { message: fallbackMessage } }

const makePass = async ({ apiSource, apiKey, apiMode, templateId, variables }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/makePass/${templateId}`,
      method: "PUT",
      data: variables ?? {}
    }

    let result = await callApi({ apiKey, apiMode, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error making passes")
  }
}

const quoteCredits = async ({ apiSource, apiKey, apiMode, templateId, eventType, numPasses }) => {
  try {
    const options = {
      url: `${apiUrls[apiSource]}/quoteCredits/${encodeURIComponent(templateId)}/${encodeURIComponent(eventType)}/${encodeURIComponent(numPasses)}`,
      method: 'GET'
    }
    return await callApi({ apiKey, apiMode, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, 'Error calculating credit cost')
  }
}

const getIssuedPasses = async ({ apiSource, apiKey, templateId, mode, startRow, maxRows, query }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/queryIssuedPasses/${templateId}`,
      method: "POST",
      data: {
        start_row: startRow,
        max_rows: maxRows,
        query,
      },
    }

    let result = await callApi({ apiKey, apiMode: mode, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting passes list")
  }
}

const getTemplatePassInstallReport = async ({ apiSource, apiKey, templateId, mode }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getTemplatePassInstallReport/${templateId}`,
      method: "GET"
    }

    let result = await callApi({ apiKey, apiMode: mode, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting template pass install report")
  }
}

const getManagedPassExport = async ({ apiSource, apiKey, templateId, mode }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/managed/data-export/${templateId}`,
      method: "GET"
    }

    let result = await callApi({ apiKey, apiMode: mode, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting managed pass export")
  }
}

const requestSyncCheck = async ({ apiSource, apiKey, templateId, apple = true, google = true }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/syncCheck/${templateId}`,
      data: {
        apple,
        google
      }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error checking synchronisation status")
  }
}

const checkSyncState = async ({ apiSource, apiKey, templateId }) => {
  try {
    let options = {
      method: 'GET',
      url: `${apiUrls[apiSource]}/checkSyncState/${templateId}`,
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting synchronisation status")
  }
}

const initiateSync = async ({ apiSource, apiKey, templateId }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/initiateSync/${templateId}`,
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error synchronising pass")
  }
}


const getPass = async ({ apiSource, apiKey, passId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getPass/${passId}`,
      method: "GET"
    }

    let result = await callApi({ apiKey, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting pass")
  }
}


const patchPass = async ({ apiSource, apiKey, passId, variables }) => {
  try {
      let options = {
        url: `${apiUrls[apiSource]}/patchPass/${passId}`,
        method: "PATCH",
        data: variables
      }

      let result = await callApi({ apiKey, options })

      return result

  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error updating pass")
  }
}

const patchPasses = async ({ apiSource, apiKey, templateId, passes = [], variables, selectionQuery, mode }) => {
  try {
    const options = {
      url: `${apiUrls[apiSource]}/patchPasses/${templateId}`,
      method: 'PATCH',
      data: {
        ...(selectionQuery ? { selection_query: selectionQuery } : { pass_ids: passes.map(pass => typeof pass === 'string' ? pass : pass.pass_id) }),
        patches: variables
      }
    }

    return await callApi({ apiKey, apiMode: mode, options })
  }
  catch (err) {
    console.error(err)
    return apiError(err, 'Error updating passes')
  }
}

const getPassNotifications = async ({ apiSource, apiKey, passId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getPassNotifications/${passId}`,
      method: "GET"
    }

    let result = await callApi({ apiKey, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting notifications")
  }
}

const notifyPass = async ({ apiSource, apiKey, passId, notification }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/notifyPass/${passId}`,
      method: "POST",
      data: { notification }
    }

    let result = await callApi({ apiKey, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error notifying pass")
  }
}

const notifyPasses = async ({ apiSource, apiKey, templateId, passes = [], notification, selectionQuery, mode }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/notifyPasses/${templateId}`,
      method: "POST",
      data: {
        ...(selectionQuery ? { selection_query: selectionQuery } : { pass_ids: passes.map(pass => typeof pass === 'string' ? pass : pass.pass_id) }),
        notification
      }
    }

    let result = await callApi({ apiKey, apiMode: mode, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error notifying passes")
  }
}

const notifyAll = async ({ apiSource, apiKey, templateId, notification }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/notifyAll/${templateId}`,
      method: "POST",
      data: { notification }
    }

    let result = await callApi({ apiKey, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error notifying passes")
  }
}



const getActivePassesCount = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getActivePassesCount`,
      method: "get"
    }

    let result = await callApi({ apiKey, apiMode: "live", options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error getting pass count")
  }
}

const deletePasses = async ({ apiSource, apiKey, apiMode, passes }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/deletePasses`,
      method: "delete",
      data: { passes }
    }

    let result = await callApi({ apiKey, apiMode, options })

    return result
  }
  catch (err) {
    console.error(err)
    return apiError(err, "Error deleting passes")
  }
}


const callApi = async ({ apiKey, apiMode, options }) => {
  try {
    return await callAuthenticatedApi({
      apiSource: getApiSourceFromUrl(options.url),
      apiKey,
      apiMode,
      includeChildClientId: true,
      options
    })
  }
  catch (err) {
    console.error(err)
    throw err
  }
}

const getApiSourceFromUrl = url => {
  return url?.startsWith(apiUrls.local) ? 'local' : 'remote'
}


export {
  checkSyncState,
  getActivePassesCount,
  getPass,
  getIssuedPasses,
  getManagedPassExport,
  initiateSync,
  requestSyncCheck,
  getTemplatePassInstallReport,
  getPassNotifications,
  makePass,
  quoteCredits,
  notifyAll,
  notifyPass,
  notifyPasses,
  patchPass,
  patchPasses,
  deletePasses,
}
